// src/lib/loadErrorClass.js
// Shared classifier for render-path load failures, so AppErrorBoundary and
// PaneErrorBoundary agree on what is a stale deploy vs a network blip.
//
// Stale deploy: a new build shipped, the old hashed chunk is gone from Vercel,
// and the SPA rewrite serves index.html (text/html) where JS was expected.
// A reload pulls the fresh index.html and fixes it.
//
// Network blip: the connection itself failed (sleep/wake, flaky Wi-Fi,
// Supabase hiccup). A reload does NOT fix it — retrying when the connection
// returns does. Classifying these as stale-deploy is what caused the
// "A new version is ready" reload loop.

const STALE_CHUNK_PATTERNS = [
  'Loading chunk',                          // webpack-style, kept for safety
  'dynamically imported module',            // Chrome "Failed to fetch…" / Firefox "error loading…"
  'Unable to preload CSS',                  // Vite CSS preload after deploy
  'is not a valid JavaScript MIME type',    // SPA rewrite served index.html for a missing chunk
  'Expected a JavaScript',                  // "Expected a JavaScript module script but the server responded…"
  'Importing a module script failed',       // Safari dynamic import failure
]

const NETWORK_PATTERNS = [
  'Failed to fetch',                        // Chrome fetch TypeError
  'NetworkError',                           // Firefox fetch failure
  'Load failed',                            // Safari fetch TypeError
]

export function isStaleChunkError(message) {
  const msg = message || ''
  if (STALE_CHUNK_PATTERNS.some((p) => msg.includes(p))) return true
  // React.lazy after a swallowed preload error: main.jsx's vite:preloadError
  // handler preventDefault()s (so Vite doesn't rethrow) and schedules a reload;
  // in that window the lazy import resolves `undefined` and React throws this.
  // Only classified as stale while the reload is actually pending — the same
  // message from a genuine undefined-module bug still reports normally.
  // Engines phrase the same throw differently:
  //   Chrome/V8:     "Cannot read properties of undefined (reading 'default')"
  //   Safari/WebKit: "undefined is not an object (evaluating 'i._result.default')"
  // Match both so Safari users get the silent reload instead of a crash card.
  if (
    typeof window !== 'undefined' &&
    window.__staleReloadPending &&
    (msg.includes("reading 'default'") || msg.includes('.default'))
  ) return true
  return false
}

export function isTransientNetworkError(message) {
  const msg = message || ''
  // Stale-chunk wins: Chrome's "Failed to fetch dynamically imported module"
  // contains both signatures and must be treated as a deploy, not a blip.
  if (isStaleChunkError(msg)) return false
  return NETWORK_PATTERNS.some((p) => msg.includes(p))
}
